/**
 * Enterprise Identity & Scoped Permissions
 * 
 * This module defines the data structures for:
 * 1. Enterprise SSO integration (Okta, Azure AD, SAML, OIDC)
 * 2. Organizational hierarchy (departments, teams, groups)
 * 3. Scoped role assignments (role + where it applies)
 * 
 * Key Concept: Role + Scope = Effective Permission
 * - A user might be a Steward in the Product team
 * - But only a Strategist (view-only) in the IT team
 * - And have no access to the Finance team
 * 
 * The hierarchy flows:
 *   Organization → Department → Team → Process → Session → Item
 * 
 * Permissions cascade DOWN but can be overridden at any level.
 */

import type { ActorRole } from './roles';
import type { PermissionAction, ScopeKind, PermissionScope } from './permissions';

// ============================================================================
// Enterprise Identity Provider Types
// ============================================================================

/**
 * Supported identity provider types
 */
export type IdentityProviderType = 
  | 'okta'
  | 'azure-ad'
  | 'google-workspace'
  | 'onelogin'
  | 'ping-identity'
  | 'saml-generic'
  | 'oidc-generic'
  | 'local';  // Built-in authentication

/**
 * Identity provider configuration
 */
export interface IdentityProviderConfig {
  id: string;
  name: string;
  type: IdentityProviderType;
  enabled: boolean;
  
  // Connection settings
  domain?: string;  // e.g., "company.okta.com"
  clientId?: string;
  issuerUrl?: string;
  
  // SAML-specific
  samlMetadataUrl?: string;
  samlEntityId?: string;
  samlCertificate?: string;
  
  // OIDC-specific
  oidcDiscoveryUrl?: string;
  oidcScopes?: string[];
  
  // Attribute mapping (IDP attribute → our attribute)
  attributeMapping: {
    userId: string;          // e.g., "sub" or "email"
    email: string;           // e.g., "email"
    displayName: string;     // e.g., "name" or "preferred_username"
    firstName?: string;      // e.g., "given_name"
    lastName?: string;       // e.g., "family_name"
    groups?: string;         // e.g., "groups" or "memberOf"
    department?: string;     // e.g., "department"
    title?: string;          // e.g., "title" or "jobTitle"
    manager?: string;        // e.g., "manager"
  };
  
  // Group sync settings
  groupSync: {
    enabled: boolean;
    syncInterval?: number;   // minutes
    groupPrefix?: string;    // e.g., "peak-" to filter groups
    autoCreateGroups: boolean;
    autoAssignRoles: boolean;
  };
  
  // Role mapping rules (IDP group → Actor Role)
  defaultRoleMappings: RoleMappingRule[];
  
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Rule for mapping IDP groups to Actor Roles
 */
export interface RoleMappingRule {
  id: string;
  name: string;
  priority: number;  // Higher priority rules evaluated first
  
  // Conditions (all must match)
  conditions: {
    // Match IDP groups (supports wildcards)
    groups?: string[];      // e.g., ["Engineering-*", "Product-Owners"]
    // Match IDP attributes
    attributes?: Record<string, string | string[]>;
    // Match email domain
    emailDomains?: string[];  // e.g., ["company.com", "subsidiary.com"]
  };
  
  // Resulting assignments
  assignments: {
    role: ActorRole;
    scopeKind: ScopeKind;
    scopeId: string;        // Specific scope or '*' for all
    scopePattern?: string;  // Pattern to extract scope from group name
  }[];
  
  enabled: boolean;
}

// ============================================================================
// Organizational Hierarchy
// ============================================================================

/**
 * Types of organizational units
 */
export type OrgUnitType = 
  | 'organization'  // Top level (the company)
  | 'division'      // Major business unit
  | 'department'    // Department within a division
  | 'team'          // Team within a department
  | 'workgroup'     // Ad-hoc or cross-functional group
  | 'project';      // Project-specific grouping

/**
 * Organizational unit (department, team, etc.)
 */
export interface OrgUnit {
  id: string;
  externalId?: string;     // ID from IDP (e.g., Okta group ID)
  name: string;
  displayName: string;
  type: OrgUnitType;
  
  // Hierarchy
  parentId?: string;       // Parent org unit
  path: string[];          // Full path from root, e.g., ["org-1", "dept-eng", "team-product"]
  depth: number;           // Depth in hierarchy (0 = organization)
  
  /** Workspace this org unit belongs to (from DB workspace_id) */
  workspaceId?: string;
  
  // Metadata
  description?: string;
  costCenter?: string;
  location?: string;
  
  // Sync tracking
  syncSource?: IdentityProviderType;
  lastSyncedAt?: Date;
  
  // Status
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
  
  // Queue Settings - each org unit can act as a queue
  queueSettings?: QueueSettings;
}

/**
 * Queue settings for an organizational unit
 * When enabled, the org unit acts as a queue for item submissions
 */
export interface QueueSettings {
  /** Whether this org unit acts as a queue */
  enabled: boolean;
  /** Whether users can submit directly to this queue */
  acceptsSubmissions: boolean;
  /** Whether items need steward approval before entering shaping */
  requiresApproval: boolean;
  /**
   * Default owner for items in this queue (optional).
   *
   * Historically declared and never wired to anything. Now applied per
   * `defaultOwnerApplyTo`, and never overwrites an owner an item already has.
   */
  defaultStewardId?: string;
  /**
   * When the default owner is applied:
   *  - 'new'              — only items arriving in the queue from now on
   *  - 'new_and_existing' — also backfills items already sitting in the queue
   * Either way, an item that already has an owner keeps it.
   */
  defaultOwnerApplyTo?: 'new' | 'new_and_existing';
  /** Description shown to users when selecting this queue */
  description?: string;
}

/**
 * User's membership in an organizational unit
 */
export interface OrgUnitMembership {
  id: string;
  userId: string;
  orgUnitId: string;
  
  // Membership type
  isPrimary: boolean;      // User's main team/department
  membershipType: 'member' | 'manager' | 'lead' | 'admin';
  
  // Sync tracking
  syncSource?: IdentityProviderType;
  externalGroupId?: string;
  
  // Dates
  startDate?: Date;
  endDate?: Date;          // For temporary assignments
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Scoped Role Assignments
// ============================================================================

/**
 * A role assignment with full scope context
 * 
 * This is the core of the permission model:
 * "User X has Role Y in Scope Z"
 * 
 * Examples:
 * - "Alice is a Steward in the Product team"
 * - "Bob is a List Owner for Process P-123"
 * - "Carol is a System Admin at the organization level"
 */
export interface ScopedActorRoleAssignment {
  id: string;
  
  // Who
  subjectType: 'user' | 'orgUnit';  // Can assign to user or entire org unit
  subjectId: string;
  
  // What role
  role: ActorRole;
  
  // Where (scope)
  scopeKind: ScopeKind;
  scopeId: string;
  scopePath?: string[];    // Full path for nested scopes
  
  // Inheritance behavior
  inheritable: boolean;    // Does this cascade to child scopes?
  inherited: boolean;      // Was this inherited from a parent scope?
  inheritedFrom?: string;  // ID of the assignment this was inherited from
  
  // Grant metadata
  grantedBy: string;       // User who made the assignment
  grantReason?: string;    // Why this was granted
  grantSource: 'manual' | 'idp-sync' | 'policy' | 'system';
  
  // Temporal bounds
  effectiveFrom?: Date;    // When assignment becomes active
  effectiveUntil?: Date;   // When assignment expires
  
  // Status
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Effective Permission Resolution
// ============================================================================

/**
 * Context for permission check
 */
export interface PermissionCheckContext {
  userId: string;
  action: PermissionAction;
  
  // Target scope (where the action is being performed)
  targetScope: PermissionScope;
  
  // Optional: specific resource being accessed
  resourceType?: 'item' | 'process' | 'session' | 'list' | 'criteria';
  resourceId?: string;
  resourceOwnerId?: string;  // Who created/owns the resource
  resourceOrgUnitId?: string; // Which org unit does this resource belong to
}

/**
 * Result of permission resolution
 */
export interface PermissionResolutionResult {
  allowed: boolean;
  
  // How the decision was made
  effectiveRole?: ActorRole;
  effectiveScope: PermissionScope;
  
  // Which assignment granted this (if allowed)
  grantingAssignment?: ScopedActorRoleAssignment;
  
  // Chain of inheritance (if inherited)
  inheritanceChain?: {
    assignmentId: string;
    scope: PermissionScope;
    role: ActorRole;
  }[];
  
  // Reason for decision
  reason: string;
  
  // Additional context
  checkedAt: Date;
}

// ============================================================================
// Enterprise User Profile
// ============================================================================

/**
 * Extended user profile with enterprise identity info
 */
export interface EnterpriseUserProfile {
  id: string;
  
  // Basic identity
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  
  // Enterprise identity
  identityProvider: IdentityProviderType;
  externalUserId?: string;  // ID from IDP
  upn?: string;             // User Principal Name (e.g., user@company.com)
  
  // Organizational context
  primaryOrgUnitId?: string;
  orgUnitMemberships: OrgUnitMembership[];
  
  // Enterprise attributes (from IDP)
  department?: string;
  title?: string;
  employeeId?: string;
  managerId?: string;
  location?: string;
  
  // Role assignments
  roleAssignments: ScopedActorRoleAssignment[];
  
  // Session/Auth info
  lastLoginAt?: Date;
  sessionExpiresAt?: Date;
  mfaEnabled?: boolean;
  
  // Sync tracking
  lastSyncedAt?: Date;
  syncErrors?: string[];
  
  // Status
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// IDP Sync Events
// ============================================================================

/**
 * Types of sync events
 */
export type SyncEventType = 
  | 'user_created'
  | 'user_updated'
  | 'user_deactivated'
  | 'user_reactivated'
  | 'group_created'
  | 'group_updated'
  | 'group_deleted'
  | 'membership_added'
  | 'membership_removed'
  | 'role_assigned'
  | 'role_revoked'
  | 'sync_started'
  | 'sync_completed'
  | 'sync_failed';

/**
 * Record of an IDP sync event
 */
export interface SyncEvent {
  id: string;
  eventType: SyncEventType;
  
  // Source
  identityProviderId: string;
  
  // Subject
  subjectType: 'user' | 'group' | 'membership' | 'system';
  subjectId?: string;
  externalSubjectId?: string;
  
  // Details
  details: Record<string, unknown>;
  
  // Result
  success: boolean;
  errorMessage?: string;
  
  // Timing
  occurredAt: Date;
  processedAt?: Date;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build a scope path from org unit hierarchy
 */
export function buildScopePath(orgUnit: OrgUnit): PermissionScope {
  return {
    kind: orgUnitTypeToScopeKind(orgUnit.type),
    id: orgUnit.id,
    name: orgUnit.displayName,
    parentScope: orgUnit.parentId ? {
      kind: 'group',  // Simplified - would need parent lookup
      id: orgUnit.parentId,
    } : undefined,
  };
}

/**
 * Map org unit type to scope kind
 */
export function orgUnitTypeToScopeKind(type: OrgUnitType): ScopeKind {
  switch (type) {
    case 'organization':
      return 'organization';
    case 'division':
    case 'department':
    case 'team':
    case 'workgroup':
    case 'project':
      return 'group';
    default:
      return 'group';
  }
}

/**
 * Check if a scope is a descendant of another scope
 */
export function isScopeDescendant(
  childPath: string[],
  parentPath: string[]
): boolean {
  if (childPath.length <= parentPath.length) return false;
  return parentPath.every((id, index) => childPath[index] === id);
}

/**
 * Get the most specific role assignment for a user at a scope
 */
export function getMostSpecificAssignment(
  assignments: ScopedActorRoleAssignment[],
  targetScopePath: string[]
): ScopedActorRoleAssignment | undefined {
  // Filter to active, applicable assignments
  const applicable = assignments.filter(a => {
    if (!a.active) return false;
    if (a.effectiveFrom && a.effectiveFrom > new Date()) return false;
    if (a.effectiveUntil && a.effectiveUntil < new Date()) return false;
    
    // Check if assignment scope is on the path to target
    const assignmentPath = a.scopePath || [a.scopeId];
    return targetScopePath.slice(0, assignmentPath.length).every(
      (id, i) => assignmentPath[i] === id || assignmentPath[i] === '*'
    );
  });
  
  if (applicable.length === 0) return undefined;
  
  // Sort by specificity (deeper = more specific)
  return applicable.sort((a, b) => {
    const aDepth = a.scopePath?.length || 1;
    const bDepth = b.scopePath?.length || 1;
    return bDepth - aDepth;  // Most specific first
  })[0];
}

// ============================================================================
// Default Identity Provider Configurations
// ============================================================================

/**
 * Template configurations for common identity providers
 */
export const IDP_TEMPLATES: Record<IdentityProviderType, Partial<IdentityProviderConfig>> = {
  'okta': {
    type: 'okta',
    attributeMapping: {
      userId: 'sub',
      email: 'email',
      displayName: 'name',
      firstName: 'given_name',
      lastName: 'family_name',
      groups: 'groups',
      department: 'department',
      title: 'title',
    },
    groupSync: {
      enabled: true,
      syncInterval: 60,
      autoCreateGroups: true,
      autoAssignRoles: true,
    },
  },
  'azure-ad': {
    type: 'azure-ad',
    attributeMapping: {
      userId: 'oid',
      email: 'email',
      displayName: 'name',
      firstName: 'given_name',
      lastName: 'family_name',
      groups: 'groups',
      department: 'department',
      title: 'jobTitle',
      manager: 'manager',
    },
    groupSync: {
      enabled: true,
      syncInterval: 60,
      autoCreateGroups: true,
      autoAssignRoles: true,
    },
  },
  'google-workspace': {
    type: 'google-workspace',
    attributeMapping: {
      userId: 'sub',
      email: 'email',
      displayName: 'name',
      firstName: 'given_name',
      lastName: 'family_name',
      groups: 'groups',
    },
    groupSync: {
      enabled: true,
      syncInterval: 60,
      autoCreateGroups: true,
      autoAssignRoles: false,
    },
  },
  'onelogin': {
    type: 'onelogin',
    attributeMapping: {
      userId: 'id',
      email: 'email',
      displayName: 'name',
      firstName: 'firstname',
      lastName: 'lastname',
      groups: 'memberOf',
      department: 'department',
      title: 'title',
    },
    groupSync: {
      enabled: true,
      syncInterval: 60,
      autoCreateGroups: true,
      autoAssignRoles: true,
    },
  },
  'ping-identity': {
    type: 'ping-identity',
    attributeMapping: {
      userId: 'sub',
      email: 'email',
      displayName: 'name',
      firstName: 'given_name',
      lastName: 'family_name',
      groups: 'memberOf',
    },
    groupSync: {
      enabled: true,
      syncInterval: 60,
      autoCreateGroups: true,
      autoAssignRoles: true,
    },
  },
  'saml-generic': {
    type: 'saml-generic',
    attributeMapping: {
      userId: 'NameID',
      email: 'email',
      displayName: 'displayName',
      firstName: 'firstName',
      lastName: 'lastName',
      groups: 'groups',
    },
    groupSync: {
      enabled: false,
      autoCreateGroups: false,
      autoAssignRoles: false,
    },
  },
  'oidc-generic': {
    type: 'oidc-generic',
    oidcScopes: ['openid', 'profile', 'email', 'groups'],
    attributeMapping: {
      userId: 'sub',
      email: 'email',
      displayName: 'name',
      firstName: 'given_name',
      lastName: 'family_name',
      groups: 'groups',
    },
    groupSync: {
      enabled: false,
      autoCreateGroups: false,
      autoAssignRoles: false,
    },
  },
  'local': {
    type: 'local',
    attributeMapping: {
      userId: 'id',
      email: 'email',
      displayName: 'name',
    },
    groupSync: {
      enabled: false,
      autoCreateGroups: false,
      autoAssignRoles: false,
    },
  },
};
